import { useTranslation } from '../context/TranslationContext';
import StaticPageShell, { StaticSection } from '../components/StaticPageShell';

function tf(t: (key: string) => string, key: string, fallback: string): string {
    const value = t(key);
    return value === key ? fallback : value;
}

/** Full privacy notice for the Petito marketplace (Belgium). */
const PrivacyPolicy = () => {
    const { t } = useTranslation();

    return (
        <StaticPageShell
            eyebrow={tf(t, 'page.privacypolicy.eyebrow', 'Legal')}
            title={tf(t, 'page.privacypolicy.title', 'Privacy Policy')}
            subtitle={tf(
                t,
                'page.privacypolicy.updated',
                'Last updated: August 2026. Applies to the Petito marketplace in Belgium.'
            )}
            maxWidth="3xl"
        >
            <p className="rounded-2xl border border-[color-mix(in_srgb,var(--accent-color),transparent_55%)] bg-[color-mix(in_srgb,var(--bg-color),white_40%)] px-5 py-4 text-sm leading-relaxed text-[var(--text-color)]/90 sm:text-base">
                {tf(
                    t,
                    'page.privacypolicy.intro',
                    'Petito ("we", "us") operates a peer-to-peer marketplace for pre-loved baby and children\'s items. This Privacy Policy explains which personal data we collect, why we process it, who we share it with, and your rights under the GDPR and Belgian law. Controller contact details appear in the site footer (Contact email).'
                )}
            </p>

            <StaticSection
                icon="🏢"
                title={tf(t, 'page.privacypolicy.controller', '1. Data controller')}
            >
                <p>
                    {tf(
                        t,
                        'page.privacypolicy.controller.text',
                        'The data controller is the operator of the Petito platform as identified in the website footer and terms of service. For privacy requests, email support@petito.be.'
                    )}
                </p>
            </StaticSection>

            <StaticSection
                icon="📂"
                title={tf(t, 'page.privacypolicy.data', '2. Categories of data we process')}
            >
                <ul>
                    <li>
                        {tf(
                            t,
                            'page.privacypolicy.data.account',
                            'Account data: email, username, password hash (if you use email/password), preferred language, email confirmation status. If you use Continue with Google: email, Google account identifier, and basic name/profile fields Google provides for sign-in.'
                        )}
                    </li>
                    <li>
                        {tf(
                            t,
                            'page.privacypolicy.data.profile',
                            'Profile & shipping: display name, profile picture URL, shipping address (street, city, postal code, country), region shown on listings when you provide it.'
                        )}
                    </li>
                    <li>
                        {tf(
                            t,
                            'page.privacypolicy.data.marketplace',
                            'Marketplace activity: product listings, photos, descriptions, prices, conditions, categories, favourites, orders, ratings, reports, support tickets, partner inquiries.'
                        )}
                    </li>
                    <li>
                        {tf(
                            t,
                            'page.privacypolicy.data.children',
                            'My Journey / child data: child name or nickname, birth or expected birth date, optional height/weight, checklist progress. This data is used only for personalisation of the journey experience and product recommendations. Do not upload unnecessary medical or highly sensitive information.'
                        )}
                    </li>
                    <li>
                        {tf(
                            t,
                            'page.privacypolicy.data.tax',
                            'Tax / DAC7-related data: when seller activity reaches legal thresholds, we may process tax identification numbers and related reporting fields required for platform seller reporting (EU DAC7 framework as applied in Belgium).'
                        )}
                    </li>
                    <li>
                        {tf(
                            t,
                            'page.privacypolicy.data.tech',
                            'Technical data: IP address and security logs as needed for authentication, rate limiting, abuse prevention; cookie/consent preference on your device.'
                        )}
                    </li>
                </ul>
            </StaticSection>

            <StaticSection
                icon="🎯"
                title={tf(t, 'page.privacypolicy.purposes', '3. Purposes and legal bases')}
            >
                <ul>
                    <li>
                        {tf(
                            t,
                            'page.privacypolicy.purposes.contract',
                            'Performance of the marketplace contract: account (including Continue with Google), listings, checkout, orders, delivery coordination, support tickets (GDPR Art. 6(1)(b)).'
                        )}
                    </li>
                    <li>
                        {tf(
                            t,
                            'page.privacypolicy.purposes.legal',
                            'Legal obligations: tax platform reporting where applicable, accounting and fraud prevention duties (Art. 6(1)(c)).'
                        )}
                    </li>
                    <li>
                        {tf(
                            t,
                            'page.privacypolicy.purposes.li',
                            'Legitimate interests: platform security, moderation of listings/images, preventing abuse, improving service reliability, seller ratings transparency (Art. 6(1)(f)). You may object where applicable.'
                        )}
                    </li>
                    <li>
                        {tf(
                            t,
                            'page.privacypolicy.purposes.consent',
                            'Consent: non-essential cookies or marketing communications if we introduce them; optional email preferences such as outgrown re-list tips (Art. 6(1)(a)). You can withdraw consent at any time without affecting prior lawful processing.'
                        )}
                    </li>
                </ul>
            </StaticSection>

            <StaticSection
                id="children"
                icon="👶"
                title={tf(t, 'page.privacypolicy.children', '4. Children and My Journey data')}
            >
                <p>
                    {tf(
                        t,
                        'page.privacypolicy.children.text',
                        'My Journey / My Petito is optional. The shop works without a child profile. When you add one, we process a nickname, birth or expected birth date, optional height and weight, and checklist progress — only to personalise sizes, phases, and outgrown tips you opted into. Legal basis: your consent as parent or guardian (GDPR Art. 6(1)(a)), withdrawable by deleting the child or your account. This is not a health record. We do not sell this data. We do not knowingly create independent accounts for children under 16. Use nicknames if you prefer.'
                    )}
                </p>
            </StaticSection>

            <StaticSection
                id="tax"
                icon="📊"
                title={tf(t, 'page.privacypolicy.taxinfo', '5. Tax information (DAC7)')}
            >
                <p>
                    {tf(
                        t,
                        'page.privacypolicy.taxinfo.text',
                        'If your selling activity on the platform reaches applicable EU/Belgian reporting thresholds (for example number of sales or total consideration in a tax year), we may be required to collect and report seller identification and transaction summary data to tax authorities. We will prompt you for required tax identifiers when you are flagged. This processing is based on legal obligation.'
                    )}
                </p>
            </StaticSection>

            <StaticSection
                id="profile"
                icon="👤"
                title={tf(t, 'page.privacypolicy.profile', '6. Public profile and listings')}
            >
                <p>
                    {tf(
                        t,
                        'page.privacypolicy.profile.text',
                        'Username (or display name), seller ratings, listing photos and text, and region (and city if you enable location features) may be visible to other users to enable trust and discovery. Full street address is used for fulfilment and is not shown as a public listing address. Do not put private contact details in listing descriptions if you do not want them public.'
                    )}
                </p>
            </StaticSection>

            <StaticSection
                icon="🔗"
                title={tf(t, 'page.privacypolicy.processors', '7. Processors and recipients')}
            >
                <p>
                    {tf(
                        t,
                        'page.privacypolicy.processors.text',
                        'We use service providers that process data on our behalf under contracts, including: hosting and database infrastructure; Amazon Web Services for image storage and (where enabled) automated image moderation; Shopify for payment checkout and order processing; email delivery (SMTP) for transactional messages. We may share data with competent authorities when legally required (e.g. tax reporting). We do not sell your personal data.'
                    )}
                </p>
                <p className="mt-3">
                    {tf(
                        t,
                        'page.privacypolicy.processors.google',
                        'Sign in with Google: if you choose Continue with Google, Google authenticates you and shares with Petito your email address, a unique Google account identifier, and basic profile name information needed to create or recognise your shop account. We store that link so you can sign in again with Google. If a Petito account already exists for that email, we link Google sign-in to that account. We do not receive your Google password and do not access other Google services (such as Gmail). Google’s own processing is described in Google’s Privacy Policy. Legal basis: performance of the contract to provide your marketplace account (GDPR Art. 6(1)(b)). Google login is for the shop only; it does not grant access to the Petito admin panel.'
                    )}
                </p>
            </StaticSection>

            <StaticSection
                icon="🌍"
                title={tf(t, 'page.privacypolicy.transfers', '8. International transfers')}
            >
                <p>
                    {tf(
                        t,
                        'page.privacypolicy.transfers.text',
                        'Some processors may process data outside the EEA. Where that occurs, we rely on appropriate safeguards such as Standard Contractual Clauses or an adequacy decision, as required by GDPR Chapter V.'
                    )}
                </p>
            </StaticSection>

            <StaticSection
                icon="⏱️"
                title={tf(t, 'page.privacypolicy.retention', '9. Retention')}
            >
                <p>
                    {tf(
                        t,
                        'page.privacypolicy.retention.text',
                        'We keep account and order data for as long as your account is active and thereafter as needed for legal claims, accounting, and tax retention periods. Listing images remain while the listing is live and for a reasonable period after. Child journey data is kept while you use My Journey or until you delete it/your account. Support tickets and partner inquiries are retained for operational follow-up and then deleted or anonymised. Exact periods may vary by legal requirement.'
                    )}
                </p>
            </StaticSection>

            <StaticSection
                icon="✋"
                title={tf(t, 'page.privacypolicy.rights', '10. Your rights')}
            >
                <p>
                    {tf(
                        t,
                        'page.privacypolicy.rights.text',
                        'You have the right to access, rectify, erase, restrict, and port your personal data, and to object to certain processing, under GDPR Articles 15–21. Where processing is based on consent, you may withdraw consent at any time. To exercise rights, contact us at support@petito.be. You may lodge a complaint with the Belgian Data Protection Authority (Gegevensbeschermingsautoriteit / Autorité de protection des données — APD-GBA).'
                    )}
                </p>
            </StaticSection>

            <StaticSection
                id="cookies"
                icon="🍪"
                title={tf(t, 'page.privacypolicy.cookies.title', '11. Cookies')}
            >
                <p>
                    {tf(
                        t,
                        'page.privacypolicy.cookies.intro',
                        'This section explains how Petito uses cookies and similar technologies. It applies to users in Belgium and is designed to comply with the GDPR and the Belgian Law of 13 June 2005 on electronic communications (as amended).'
                    )}
                </p>
                <h3>{tf(t, 'page.privacypolicy.cookies.what.title', 'What are cookies?')}</h3>
                <p>
                    {tf(
                        t,
                        'page.privacypolicy.cookies.what.text',
                        'Cookies are small text files stored on your device when you visit a website. They help the site function properly, keep you signed in, and protect your account.'
                    )}
                </p>
                <h3>
                    {tf(t, 'page.privacypolicy.cookies.essential.title', 'Essential cookies we use')}
                </h3>
                <p>
                    {tf(
                        t,
                        'page.privacypolicy.cookies.essential.text',
                        'Petito currently uses only strictly necessary (essential) cookies. These are required for authentication, session management, security, and remembering your cookie consent choice. Without these cookies, core features such as logging in and managing your account would not work.'
                    )}
                </p>
                <ul>
                    <li>
                        {tf(
                            t,
                            'page.privacypolicy.cookies.essential.auth',
                            'Authentication cookies — keep you securely logged in during your visit.'
                        )}
                    </li>
                    <li>
                        {tf(
                            t,
                            'page.privacypolicy.cookies.essential.consent',
                            'Consent cookie / local storage — stores your cookie preference so we do not ask again on every visit.'
                        )}
                    </li>
                </ul>
                <h3>
                    {tf(
                        t,
                        'page.privacypolicy.cookies.optional.title',
                        'Optional and third-party cookies'
                    )}
                </h3>
                <p>
                    {tf(
                        t,
                        'page.privacypolicy.cookies.optional.text',
                        'We do not currently place optional analytics, advertising, or social-media tracking cookies. If this changes (for example Meta advertising pixels), we will update this policy and request your consent before setting any non-essential cookies, in line with Belgian opt-in requirements.'
                    )}
                </p>
                <h3>
                    {tf(t, 'page.privacypolicy.cookies.manage.title', 'Managing your preferences')}
                </h3>
                <p>
                    {tf(
                        t,
                        'page.privacypolicy.cookies.manage.text',
                        'When you first visit Petito, you can accept, reject, or manage cookie preferences via our cookie banner. You may withdraw consent at any time by clearing your browser cookies and local storage, or by contacting us. Essential cookies cannot be disabled while using the platform.'
                    )}
                </p>
                <h3>
                    {tf(t, 'page.privacypolicy.cookies.retention.title', 'Cookie retention')}
                </h3>
                <p>
                    {tf(
                        t,
                        'page.privacypolicy.cookies.retention.text',
                        'Session cookies are deleted when you close your browser. Your consent preference is stored locally until you clear it or until we update our cookie practices.'
                    )}
                </p>
            </StaticSection>

            <StaticSection
                icon="🔐"
                title={tf(t, 'page.privacypolicy.security', '12. Security')}
            >
                <p>
                    {tf(
                        t,
                        'page.privacypolicy.security.text',
                        'We implement technical and organisational measures appropriate to the risk, including encrypted transport (HTTPS), password hashing, separated admin and shop authentication, and access controls. No method of transmission is 100% secure.'
                    )}
                </p>
            </StaticSection>

            <StaticSection
                icon="📝"
                title={tf(t, 'page.privacypolicy.changes', '13. Changes')}
            >
                <p>
                    {tf(
                        t,
                        'page.privacypolicy.changes.text',
                        'We may update this Privacy Policy from time to time. Material changes will be reflected by updating the “Last updated” date and, where appropriate, a notice on the site.'
                    )}
                </p>
            </StaticSection>

            <StaticSection
                icon="✉️"
                title={tf(
                    t,
                    'page.privacypolicy.cookies.contact.title',
                    '14. Contact & supervisory authority'
                )}
            >
                <p>
                    {tf(
                        t,
                        'page.privacypolicy.cookies.contact.text',
                        'For questions about this Privacy Policy or to exercise your GDPR rights, contact us at support@petito.be. You also have the right to lodge a complaint with the Belgian Data Protection Authority (Gegevensbeschermingsautoriteit / APD-GBA).'
                    )}
                </p>
            </StaticSection>

        </StaticPageShell>
    );
};

export default PrivacyPolicy;
